import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Users, ThumbsUp, ArrowLeft } from 'lucide-react';
import type { Game } from '../types';

// Mock trending data
const trendingGames: Game[] = [
  {
    id: 2090710,
    name: "Helldivers 2",
    playerCount: 458000,
    categories: ["Action", "Shooter", "Co-op"],
    imageUrl: "https://cdn.cloudflare.steamstatic.com/steam/apps/2090710/header.jpg",
    description: "A hardcore cooperative shooter where you fight for a Super Earth.",
    rating: {
      positive: 95000,
      negative: 5000,
      total: 100000,
      percentage: 95
    },
    monthlyStats: [
      {
        month: "2024-02",
        averagePlayers: 458000,
        peakPlayers: 520000,
        gain: 458000,
        gainPercentage: 100
      }
    ]
  },
  {
    id: 1966720,
    name: "Palworld",
    playerCount: 350000,
    categories: ["Survival", "Open World", "Multiplayer"],
    imageUrl: "https://cdn.cloudflare.steamstatic.com/steam/apps/1966720/header.jpg",
    description: "Mysterious creatures with incredible powers await in this open-world survival crafting game.",
    rating: {
      positive: 380000,
      negative: 40000,
      total: 420000,
      percentage: 90
    },
    monthlyStats: [
      {
        month: "2024-02",
        averagePlayers: 350000,
        peakPlayers: 450000,
        gain: 350000,
        gainPercentage: 85
      }
    ]
  },
  {
    id: 1938090,
    name: "Enshrouded",
    playerCount: 158000,
    categories: ["Survival", "RPG", "Open World"],
    imageUrl: "https://cdn.cloudflare.steamstatic.com/steam/apps/1938090/header.jpg",
    description: "Survive in a world covered by a mysterious and corrupting fog.",
    rating: {
      positive: 140000,
      negative: 20000,
      total: 160000,
      percentage: 87
    },
    monthlyStats: [
      {
        month: "2024-02",
        averagePlayers: 158000,
        peakPlayers: 200000,
        gain: 158000,
        gainPercentage: 75
      }
    ]
  }
];

export function TrendingPage() {
  const navigate = useNavigate();

  const getRatingColor = (percentage: number): string => {
    if (percentage >= 95) return 'text-emerald-600';
    if (percentage >= 85) return 'text-blue-600';
    if (percentage >= 70) return 'text-indigo-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-emerald-600" />
            <h1 className="text-2xl font-bold text-gray-900">Trending on Steam</h1>
          </div>
        </div>

        <div className="grid gap-6">
          {trendingGames.map((game) => (
            <div
              key={game.id}
              onClick={() => navigate(`/game/${game.id}`)}
              className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-all transform hover:scale-[1.01]"
            >
              <div className="md:flex">
                <div className="md:w-72 h-48 md:h-auto">
                  <img
                    src={game.imageUrl}
                    alt={game.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <h2 className="text-xl font-bold text-gray-900">{game.name}</h2>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <span className="font-semibold text-emerald-600">
                        +{Math.round(game.monthlyStats?.[0]?.gainPercentage || 0)}%
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4 line-clamp-2">{game.description}</p>
                  
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <Users className="w-5 h-5 text-gray-500 mr-2" />
                      <span className="font-medium">{game.playerCount.toLocaleString()} players</span>
                    </div>
                    
                    {game.rating && (
                      <div className="flex items-center">
                        <ThumbsUp className={`w-5 h-5 mr-2 ${getRatingColor(game.rating.percentage)}`} />
                        <span className={`font-medium ${getRatingColor(game.rating.percentage)}`}>
                          {game.rating.percentage}% positive
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {game.categories?.map((category) => (
                      <span
                        key={category}
                        className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-sm"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}